import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Sparkles, X, Send, Loader2, Bot, Mic, Square, Settings, VolumeX, Languages } from 'lucide-react';
import axios from 'axios';
import baseURL from '../../utils/baseURL';
import { sendAssistantMessage, sendAssistantVoice } from '../../redux/assistantSlice';

// Pages that already own the bottom-right corner with a message composer.
//
// The floating button is `fixed bottom-5 right-5 z-[900]`, which lands exactly
// on top of a chat page's Send button. It does not merely look wrong: the
// button sits ABOVE the composer in the stacking order, so it swallows the
// click and Send never fires.
const COMPOSER_ROUTES = [/^\/patientchat/, /^\/doctorchat/, /^\/chat/, /^\/support/];

// "Hey Mitra" / "Hi Mitra" / "Hello Mitra" / "OK Mitra". Speech-to-text mangles
// a name it does not know, so accept the near-misses too — a wake word that
// only fires on perfect transcription feels broken.
const WAKE_PHRASE = /\b(hey|hi|hello|ok|okay)\s*[,]?\s*(mitra|mithra|meetra|metra|mitre|meter|metro)\b/i;

// Spoken "I'm done" — ends the hands-free loop without needing the mouse.
const STOP_PHRASE = /^\s*(stop|quit|exit|cancel|bye|goodbye|thanks|thank you|that'?s all|nothing)\b/i;

const SpeechRecognitionCtor =
    typeof window !== 'undefined'
        ? window.SpeechRecognition || window.webkitSpeechRecognition
        : null;

const ttsSupported = typeof window !== 'undefined' && 'speechSynthesis' in window;

// ---- Silence detection ------------------------------------------------
// The recorder used to run until the user clicked Stop, which is unusable
// hands-free: after the wake word you had to reach for the mouse to end your
// own sentence. These drive an amplitude gate instead.
const SPEECH_RMS = 0.025;   // above this counts as someone talking
const SILENCE_MS = 1300;    // hang up this long after they stop
const NO_SPEECH_MS = 7000;  // nobody said anything — give up quietly
const MAX_RECORD_MS = 25000;

let EMOJI_RE = null;
try {
    EMOJI_RE = new RegExp('[\\p{Extended_Pictographic}\\p{Emoji_Presentation}]', 'gu');
} catch { EMOJI_RE = null; }

const FEMALE_HINTS = /(female|woman|samantha|victoria|karen|moira|tessa|fiona|zira|susan|allison|ava|serena|catherine|amelie|amélie|anna|joana|paulina|google uk english female|google us english)/i;
const MALE_HINTS = /(male|man|daniel|alex|fred|thomas|oliver|rishi|george|david|mark|james|arthur|google uk english male)/i;

// Warm, soft voices first. Left to chance the browser hands back whatever comes
// first alphabetically — usually a flat robotic eSpeak/Microsoft voice, which
// is what "I don't like the male voice" was about. These are the names of the
// neural / natural voices that actually ship on Windows, macOS, iOS and
// Android, and they sound human rather than synthesised.
const SOFT_FIRST = /(natural|neural|premium|enhanced|siri|samantha|ava|serena|aria|jenny|guy|ryan|arthur|daniel|libby|sonia|google)/i;
const HARSH_LAST = /(espeak|festival|pico|compact|robot|whisper|zarvox|bells|bahh|boing|trinoids|albert|bad news|good news)/i;

// The assistant answers in whatever language it was asked in — ask in Tamil and
// the reply comes back in Tamil. The voice used to be chosen from the app's UI
// language instead, so a Tamil sentence was handed to an English voice, which
// reads it as nonsense syllables.
//
// Script detection is the reliable signal here: Tamil, Devanagari, Bengali and
// the rest each occupy their own Unicode block, so counting characters says
// what the text IS, with no guessing and no extra request. Latin-script
// languages (English vs French) cannot be told apart this way, so those fall
// back to the interface language, which is correct for them.
const SCRIPT_LANGS = [
    ['ta', /[஀-௿]/g],   // Tamil
    ['hi', /[ऀ-ॿ]/g],   // Devanagari — Hindi/Marathi
    ['te', /[ఀ-౿]/g],   // Telugu
    ['kn', /[ಀ-೿]/g],   // Kannada
    ['ml', /[ഀ-ൿ]/g],   // Malayalam
    ['bn', /[ঀ-৿]/g],   // Bengali
    ['gu', /[઀-૿]/g],   // Gujarati
    ['pa', /[਀-੿]/g],   // Gurmukhi — Punjabi
    ['or', /[଀-୿]/g],   // Odia
    ['si', /[඀-෿]/g],   // Sinhala
    ['ar', /[؀-ۿ]/g],   // Arabic
    ['he', /[֐-׿]/g],   // Hebrew
    ['th', /[฀-๿]/g],   // Thai
    ['ru', /[Ѐ-ӿ]/g],   // Cyrillic
    ['el', /[Ͱ-Ͽ]/g],   // Greek
    ['ko', /[가-힯]/g],   // Hangul
    ['ja', /[぀-ヿ]/g],   // Kana
    ['zh', /[一-鿿]/g],   // Han
];

// A regional tag helps platforms that only register voices under a full locale.
const LOCALE_OF = {
    ta: 'ta-IN', hi: 'hi-IN', te: 'te-IN', kn: 'kn-IN', ml: 'ml-IN', bn: 'bn-IN',
    gu: 'gu-IN', pa: 'pa-IN', or: 'or-IN', si: 'si-LK', ar: 'ar-SA', he: 'he-IL',
    th: 'th-TH', ru: 'ru-RU', el: 'el-GR', ko: 'ko-KR', ja: 'ja-JP', zh: 'zh-CN',
};

function detectSpeechLang(text, fallback) {
    const s = String(text || '');
    let best = null;
    let bestCount = 0;
    for (const [lang, re] of SCRIPT_LANGS) {
        const n = (s.match(re) || []).length;
        if (n > bestCount) { bestCount = n; best = lang; }
    }
    // A stray character is not a language. Require a real run of the script so
    // one Tamil word quoted inside an English sentence does not switch voices.
    return bestCount >= 4 ? best : fallback;
}

function pickVoice(voices, lang, gender) {
    if (!voices || !voices.length) return null;
    const prefix = (lang || 'en').slice(0, 2).toLowerCase();
    const sameLang = voices.filter((v) => v.lang && v.lang.toLowerCase().startsWith(prefix));
    const pool = (sameLang.length ? sameLang : voices).filter((v) => !HARSH_LAST.test(v.name));
    const usable = pool.length ? pool : (sameLang.length ? sameLang : voices);

    const want = gender === 'male' ? MALE_HINTS : FEMALE_HINTS;
    const avoid = gender === 'male' ? FEMALE_HINTS : MALE_HINTS;
    const ofGender = usable.filter((v) => want.test(v.name));
    const notOther = usable.filter((v) => !avoid.test(v.name));

    // Within the right gender, prefer the softest-sounding one available.
    const matched = ofGender.find((v) => SOFT_FIRST.test(v.name)) || ofGender[0] || null;
    if (matched) return { voice: matched, genderMatched: true };

    // Nothing of the requested gender exists for this language. Indian-language
    // voices in particular carry names like "Google தமிழ்" that say nothing
    // about gender, so this is the normal case once the reply is not English.
    // Take the in-language voice anyway — correct pronunciation matters far
    // more than timbre — and let the caller shift pitch to compensate. This is
    // why the voice seemed to "suddenly turn male" partway through a chat: the
    // language changed, and with it the only available voice.
    const fallback = notOther.find((v) => SOFT_FIRST.test(v.name))
        || notOther[0]
        || usable[0]
        || null;
    return { voice: fallback, genderMatched: false };
}

// Speaking speed. The browser default (1.0) reads a clinical sentence more
// slowly than a person would say it, which is what made replies drag.
const RATE_MIN = 0.8;
const RATE_MAX = 1.6;
const RATE_DEFAULT = 1.15;

// Split keeping the separators, so joining a prefix reproduces the original
// spacing exactly — revealing "word by word" must not reflow the paragraph.
const wordTokens = (text) => String(text || '').split(/(\s+)/);

// Roughly how many words a synthesiser gets through per second at rate 1. Only
// used to drive the reveal when a browser gives us no boundary events (Safari
// and most mobiles fire none); Chrome's real events override it.
const WORDS_PER_SEC = 2.9;

/**
 * Turn a reply into something worth listening to.
 *
 * Read verbatim, a reply is unbearable: the voice announces "smiling face with
 * smiling eyes" for every 😊, spells out a stray `OPTIONS: [...]` line the
 * model sometimes leaves in, and then recites all thirty-six appointment times
 * one by one. On screen those are fine — chips render them. Aloud they are
 * noise, so the spoken copy drops them and simply asks the user to pick.
 */
function speakableText(text, options = [], askToChoose = 'Please choose one of the options below.') {
    let s = String(text || '');

    // A stray machine-readable line must never be read out.
    s = s.replace(/^\s*OPTIONS\s*:\s*\[[\s\S]*?\]\s*$/gim, ' ');

    // Emoji and pictographs. Built with the RegExp constructor rather than a
    // literal: an engine without \p{...} support throws while PARSING a
    // literal, which would take the whole bundle down instead of just
    // degrading the voice.
    if (EMOJI_RE) s = s.replace(EMOJI_RE, ' ');
    s = s.replace(/[️‍]/g, '');   // variation selector + zero-width joiner

    // Leftover markdown the model keeps emitting.
    s = s.replace(/[*_`#]/g, ' ');

    // More than a couple of choices: say the lead-in, not the list. Two is the
    // limit because "yes or no" is natural to hear; ten dates are not.
    if (Array.isArray(options) && options.length > 2) {
        s = s.replace(/^\s*(?:\d{1,2}[.)]|[-*•])\s+.*$/gm, ' ');
        s = `${s.trim()} ${askToChoose}`;
    }

    // Drop the empty shells the removals leave behind — a synthesiser pauses
    // awkwardly on every blank line it is handed.
    return s
        .split('\n')
        .map((line) => line.replace(/[ \t]{2,}/g, ' ').trim())
        .filter(Boolean)
        .join('\n')
        .trim();
}

const readPref = (key, fallback) => {
    try {
        const v = localStorage.getItem(key);
        return v === null ? fallback : v === 'true';
    } catch { return fallback; }
};

export default function FloatingAssistant() {
    const { t, i18n } = useTranslation(['assistant']);
    const dispatch = useDispatch();
    const { pathname } = useLocation();
    const role = typeof window !== 'undefined' ? localStorage.getItem('Role') : null;

    const [open, setOpen] = useState(false);
    const [input, setInput] = useState('');
    const [busy, setBusy] = useState(false);
    const [messages, setMessages] = useState([]);
    const sessionIdRef = useRef(`s-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
    const bottomRef = useRef(null);

    // ---- Voice input ----
    // Declared with the other hooks ON PURPOSE: hooks after an early return is
    // a rules-of-hooks violation, and the route/wake-word returns below do vary
    // between renders.
    const [recording, setRecording] = useState(false);
    const [transcribing, setTranscribing] = useState(false);
    const recorderRef = useRef(null);
    const chunksRef = useRef([]);
    const monitorRef = useRef(null);      // { ctx, raf, stream } for silence detection
    const recordingRef = useRef(false);
    const discardRef = useRef(false);     // stop() because nobody spoke

    // ---- Voice output + settings ----
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [speakReplies, setSpeakReplies] = useState(() => readPref('assistantSpeak', true));
    const [wakeWord, setWakeWord] = useState(() => readPref('assistantWake', false));
    const [voiceGender, setVoiceGender] = useState(() => {
        try { return localStorage.getItem('assistantVoiceGender') || 'female'; } catch { return 'female'; }
    });
    const [voiceRate, setVoiceRate] = useState(() => {
        try {
            const n = parseFloat(localStorage.getItem('assistantVoiceRate'));
            return Number.isFinite(n) && n >= RATE_MIN && n <= RATE_MAX ? n : RATE_DEFAULT;
        } catch { return RATE_DEFAULT; }
    });
    const [voices, setVoices] = useState([]);
    const [speaking, setSpeaking] = useState(false);
    const [wakeListening, setWakeListening] = useState(false);
    const [lastHeard, setLastHeard] = useState('');
    const [handsFree, setHandsFree] = useState(false);
    // Word-by-word reveal, so the text appears as it is spoken instead of
    // landing complete and then being read back at you.
    // { key: index of the message, shown: how many word-tokens are visible }
    const [reveal, setReveal] = useState(null);
    const revealTimerRef = useRef(null);
    // Set when the reply's language has no voice installed on this device.
    const [missingVoiceLang, setMissingVoiceLang] = useState(null);
    // Which language the ASSISTANT answers in. Read from the language master
    // (Super Admin -> Master -> Language), so the list is whatever the platform
    // actually supports rather than a hardcoded one that drifts.
    //
    // This is scoped to the assistant on purpose: the backend drops it for any
    // other surface, so choosing a language here can never change how AI
    // Reports or any other AI feature reads.
    const [replyLang, setReplyLang] = useState(() => {
        try { return localStorage.getItem('assistantReplyLang') || ''; } catch { return ''; }
    });
    const [langOpen, setLangOpen] = useState(false);
    const [langs, setLangs] = useState([]);

    // Which language the USER speaks. 'auto' lets Whisper guess, which is what
    // turned Tamil speech into English text.
    const [speechLang, setSpeechLang] = useState(() => {
        try { return localStorage.getItem('assistantSpeechLang') || 'auto'; } catch { return 'auto'; }
    });
    // Language of the last reply — the best automatic guess when set to 'auto'.
    const convLangRef = useRef(null);
    const recognitionRef = useRef(null);
    const spokenCountRef = useRef(0);
    const micRef = useRef(null);
    const handsFreeRef = useRef(false);

    const isAllowedRole = role === 'doctor' || role === 'patient';
    const hidden = !isAllowedRole || COMPOSER_ROUTES.some((r) => r.test(pathname));

    useEffect(() => { handsFreeRef.current = handsFree; }, [handsFree]);
    useEffect(() => { recordingRef.current = recording; }, [recording]);

    useEffect(() => {
        if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }, [messages, open]);

    // Language master — fetched once, when the picker is first opened.
    useEffect(() => {
        if (!langOpen || langs.length || hidden) return;
        let alive = true;
        axios.get(`${baseURL}/translation/language`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
        })
            .then((r) => {
                const rows = r?.data?.data;
                if (alive && Array.isArray(rows)) {
                    setLangs(rows
                        .filter((l) => l && l.name && l.code)
                        .map((l) => ({ name: l.name, code: String(l.code).toLowerCase() })));
                }
            })
            .catch(() => { /* the picker just stays on "match my message" */ });
        return () => { alive = false; };
    }, [langOpen, langs.length, hidden]);

    useEffect(() => {
        if (!ttsSupported) return undefined;
        const load = () => setVoices(window.speechSynthesis.getVoices() || []);
        load();
        window.speechSynthesis.addEventListener('voiceschanged', load);
        return () => window.speechSynthesis.removeEventListener('voiceschanged', load);
    }, []);

    const stopSpeaking = useCallback(() => {
        if (!ttsSupported) return;
        try { window.speechSynthesis.cancel(); } catch { /* noop */ }
        setSpeaking(false);
    }, []);

    // Hand the microphone back after the assistant finishes talking, so a
    // conversation continues without touching anything.
    const listenAgain = useCallback((delay = 350) => {
        if (!handsFreeRef.current) return;
        setTimeout(() => {
            if (handsFreeRef.current && !recordingRef.current) micRef.current?.();
        }, delay);
    }, []);

    const speak = useCallback((text, { options = [], then, onProgress } = {}) => {
        const clean = speakableText(
            text, options,
            t('assistant.choose_prompt', 'Please choose one of the options below.'),
        );
        if (!ttsSupported || !clean) { onProgress?.(1); then?.(); return; }
        try {
            window.speechSynthesis.cancel();
            const spoken = clean.slice(0, 800);
            const u = new SpeechSynthesisUtterance(spoken);
            // Chrome reports the character it has reached; turn that into a
            // 0..1 fraction the caller can map onto the ON-SCREEN text, which
            // is longer (it still has the emoji and the option list in it).
            if (onProgress) {
                u.onboundary = (ev) => {
                    if (!spoken.length) return;
                    const at = typeof ev.charIndex === 'number' ? ev.charIndex : 0;
                    onProgress(Math.max(0, Math.min(1, at / spoken.length)));
                };
            }
            // Speak the language the REPLY is in, not the language of the menus.
            const lang = detectSpeechLang(spoken, i18n.language || 'en');
            const { voice: v, genderMatched } = pickVoice(voices, lang, voiceGender);
            if (v) {
                u.voice = v;
                u.lang = v.lang;
            } else {
                // No installed voice for this script. Still declare the
                // language — Android and Windows can route it to a downloadable
                // engine, and it is a far better guess than reading Tamil with
                // an English voice.
                u.lang = LOCALE_OF[lang] || lang || 'en-US';
                // Tell the user rather than letting it fail mutely: a desktop
                // with no Tamil voice will simply produce nothing, and that is
                // indistinguishable from a broken feature.
                if (LOCALE_OF[lang]) setMissingVoiceLang(lang);
            }
            if (v) setMissingVoiceLang(null);
            u.rate = voiceRate;
            // Gentle, not cartoonish. A touch above neutral reads as warm and
            // caring; the flat 1.0 default sounds like a machine reading a
            // form, and going much higher sounds sarcastic.
            //
            // When the language offers no voice of the chosen gender, lean on
            // pitch harder so a Tamil reply still sounds recognisably like the
            // voice the user picked, instead of flipping to the opposite one
            // halfway through the conversation.
            const base = voiceGender === 'male' ? 0.95 : 1.15;
            const compensated = voiceGender === 'male' ? 0.75 : 1.4;
            u.pitch = genderMatched ? base : compensated;
            u.volume = 1;
            u.onstart = () => setSpeaking(true);
            u.onend = () => { setSpeaking(false); onProgress?.(1); then?.(); };
            u.onerror = () => { setSpeaking(false); onProgress?.(1); then?.(); };
            window.speechSynthesis.speak(u);
        } catch { setSpeaking(false); onProgress?.(1); then?.(); }
    }, [voices, voiceGender, voiceRate, i18n.language, t]);

    // Read out each NEW bot reply once, then hand the mic back if we are in a
    // hands-free conversation.
    useEffect(() => {
        const bots = messages.filter((m) => m.from === 'bot');
        if (bots.length <= spokenCountRef.current) { spokenCountRef.current = bots.length; return; }
        const latest = bots[bots.length - 1];
        spokenCountRef.current = bots.length;
        if (hidden || !latest?.text) return;

        // Remember what language this conversation is in, so the next thing the
        // user says is transcribed in it rather than guessed at.
        const replyLang = detectSpeechLang(latest.text, null);
        if (replyLang) convLangRef.current = replyLang;

        if (!speakReplies) { setReveal(null); listenAgain(200); return; }

        // Index of the message being spoken, so only THAT bubble is revealed.
        let key = -1;
        for (let i = messages.length - 1; i >= 0; i -= 1) {
            if (messages[i].from === 'bot') { key = i; break; }
        }
        const tokens = wordTokens(latest.text);
        const total = tokens.length;
        setReveal({ key, shown: 0 });

        // Boundary events are the accurate source, but Safari and most mobile
        // browsers never fire them. Run a clock as well and take whichever is
        // further along, so the text always moves.
        clearInterval(revealTimerRef.current);
        const startedAt = Date.now();
        const wordCount = Math.max(1, String(latest.text).trim().split(/\s+/).length);
        const estMs = (wordCount / (WORDS_PER_SEC * voiceRate)) * 1000;
        revealTimerRef.current = setInterval(() => {
            const f = Math.min(1, (Date.now() - startedAt) / Math.max(400, estMs));
            setReveal((r) => (r && r.key === key
                ? { key, shown: Math.max(r.shown, Math.ceil(f * total)) }
                : r));
        }, 90);

        speak(latest.text, {
            options: latest.options,
            onProgress: (f) => setReveal((r) => (r && r.key === key
                ? { key, shown: Math.max(r.shown, Math.ceil(f * total)) }
                : r)),
            then: () => {
                clearInterval(revealTimerRef.current);
                setReveal(null);            // null = show the message in full
                listenAgain();
            },
        });
    }, [messages, speakReplies, hidden, speak, listenAgain, voiceRate]);

    useEffect(() => () => clearInterval(revealTimerRef.current), []);

    useEffect(() => { if (!speakReplies) stopSpeaking(); }, [speakReplies, stopSpeaking]);

    // ---- Wake word ----------------------------------------------------
    useEffect(() => {
        // Paused while the recorder runs (two consumers cannot share the mic)
        // and while the assistant speaks (else it hears its own reply).
        if (!wakeWord || hidden || !SpeechRecognitionCtor || recording || transcribing || speaking) {
            if (recognitionRef.current) {
                try { recognitionRef.current.abort(); } catch { /* noop */ }
                recognitionRef.current = null;
                setWakeListening(false);
            }
            return undefined;
        }

        let stopped = false;
        let restartTimer = null;
        const rec = new SpeechRecognitionCtor();
        rec.continuous = true;
        rec.interimResults = true;
        // "Mitra" is not an English word, so the top guess is often "meet ra" or
        // "metro". Checking every alternative is what makes this fire in a real
        // room rather than only in a quiet one.
        rec.maxAlternatives = 4;
        rec.lang = i18n.language || 'en-US';

        rec.onstart = () => setWakeListening(true);
        rec.onresult = (event) => {
            let heard = '';
            for (let i = event.resultIndex; i < event.results.length; i += 1) {
                const r = event.results[i];
                for (let a = 0; a < r.length; a += 1) heard += ` ${r[a].transcript}`;
            }
            const cleaned = heard.trim();
            if (cleaned) setLastHeard(cleaned.slice(-70));
            if (WAKE_PHRASE.test(cleaned)) {
                stopped = true;
                try { rec.stop(); } catch { /* noop */ }
                setOpen(true);
                stopSpeaking();
                setHandsFree(true);
                handsFreeRef.current = true;
                // Answer INSTANTLY from the browser instead of waiting for a
                // round trip. The old flow sat silent for ~3s before recording,
                // so people repeated themselves into a mic that was not on yet.
                speak(t('assistant.wake_greeting', 'Yes? How can I help?'), {
                    then: () => listenAgain(120),
                });
                if (!ttsSupported || !speakReplies) listenAgain(120);
            }
        };
        // Chrome ends the session at every silence gap. Restart on a timer:
        // calling start() inside onend throws InvalidStateError, and swallowing
        // that left the recogniser dead after the first pause.
        const restart = () => {
            if (stopped || recognitionRef.current !== rec) return;
            clearTimeout(restartTimer);
            restartTimer = setTimeout(() => {
                if (stopped || recognitionRef.current !== rec) return;
                try { rec.start(); } catch { /* already running */ }
            }, 400);
        };
        rec.onend = () => { setWakeListening(false); restart(); };
        rec.onerror = (e) => {
            const err = e?.error;
            if (err === 'not-allowed' || err === 'service-not-allowed') {
                stopped = true;
                setWakeWord(false);
                setLastHeard('');
                try { localStorage.setItem('assistantWake', 'false'); } catch { /* noop */ }
            }
            // 'no-speech' / 'aborted' / 'network' are routine; onend restarts us.
        };

        recognitionRef.current = rec;
        try { rec.start(); } catch { /* noop */ }

        return () => {
            stopped = true;
            clearTimeout(restartTimer);
            recognitionRef.current = null;
            try { rec.abort(); } catch { /* noop */ }
            setWakeListening(false);
        };
    }, [wakeWord, hidden, recording, transcribing, speaking, i18n.language, stopSpeaking, speak, listenAgain, speakReplies, t]);

    useEffect(() => () => {
        // Unmount: never leave a hot microphone or an audio context behind.
        try { monitorRef.current?.stop?.(); } catch { /* noop */ }
        try { window.speechSynthesis?.cancel(); } catch { /* noop */ }
    }, []);

    if (hidden) return null;

    const persist = (key, value) => {
        try { localStorage.setItem(key, String(value)); } catch { /* noop */ }
    };

    const endHandsFree = () => { handsFreeRef.current = false; setHandsFree(false); };

    // Closing the panel must end the conversation completely: stop talking,
    // stop listening, and throw away whatever is half-recorded. Otherwise the
    // assistant carries on speaking to a closed window and keeps the
    // microphone open — the user thinks they hung up, and it is still there.
    //
    // The wake word deliberately survives this. That is the whole point of it:
    // closed panel, say "Hey Mitra", it comes back.
    const closePanel = () => {
        setOpen(false);
        setSettingsOpen(false);
        stopSpeaking();
        endHandsFree();
        setReveal(null);
        if (recordingRef.current) {
            discardRef.current = true;          // never transcribe a hung-up sentence
            try { recorderRef.current?.stop(); } catch { /* noop */ }
        }
    };

    const send = async (textOverride) => {
        const text = (textOverride ?? input).trim();
        if (!text || busy) return;
        setInput('');
        setMessages((m) => [...m, { from: 'user', text }]);
        setBusy(true);
        try {
            const res = await dispatch(sendAssistantMessage({
                message: text,
                sessionId: sessionIdRef.current,
                replyLang: replyLang || undefined,
            })).unwrap();
            setMessages((m) => [...m, { from: 'bot', text: res?.reply || '…', options: Array.isArray(res?.options) ? res.options : [] }]);
        } catch {
            endHandsFree();
            setMessages((m) => [...m, { from: 'bot', text: t('assistant.error', 'Sorry — I could not answer right now. Please try again.') }]);
        } finally {
            setBusy(false);
        }
    };

    // Watch the microphone level and stop the recorder when the speaker stops.
    const monitorSilence = (stream, stopFn) => {
        try {
            const Ctx = window.AudioContext || window.webkitAudioContext;
            if (!Ctx) return;
            const ctx = new Ctx();
            const source = ctx.createMediaStreamSource(stream);
            const analyser = ctx.createAnalyser();
            analyser.fftSize = 1024;
            source.connect(analyser);
            const buf = new Uint8Array(analyser.fftSize);

            const startedAt = Date.now();
            let sawSpeech = false;
            let lastLoud = Date.now();
            let raf = 0;

            const tick = () => {
                if (!recordingRef.current) return;
                analyser.getByteTimeDomainData(buf);
                let sum = 0;
                for (let i = 0; i < buf.length; i += 1) {
                    const v = (buf[i] - 128) / 128;
                    sum += v * v;
                }
                const rms = Math.sqrt(sum / buf.length);
                const now = Date.now();
                if (rms > SPEECH_RMS) { sawSpeech = true; lastLoud = now; }

                if (sawSpeech && now - lastLoud > SILENCE_MS) return stopFn(false);
                if (!sawSpeech && now - startedAt > NO_SPEECH_MS) return stopFn(true);
                if (now - startedAt > MAX_RECORD_MS) return stopFn(false);
                raf = requestAnimationFrame(tick);
                return undefined;
            };
            raf = requestAnimationFrame(tick);

            monitorRef.current = {
                stop: () => {
                    cancelAnimationFrame(raf);
                    try { source.disconnect(); } catch { /* noop */ }
                    try { ctx.close(); } catch { /* noop */ }
                    monitorRef.current = null;
                },
            };
        } catch { /* level metering is a nicety — recording still works without it */ }
    };

    const toggleMic = async () => {
        if (recordingRef.current) {
            discardRef.current = false;
            try { recorderRef.current?.stop(); } catch { /* noop */ }
            return;
        }
        stopSpeaking();

        // First time someone taps the mic in this session, say who this is and
        // that there is a hands-free way in. Nothing else advertises the wake
        // word, so without this nobody discovers it.
        let introduced = true;
        try { introduced = sessionStorage.getItem('assistantIntroduced') === 'true'; } catch { /* noop */ }
        if (!introduced) {
            try { sessionStorage.setItem('assistantIntroduced', 'true'); } catch { /* noop */ }
            handsFreeRef.current = true;
            setHandsFree(true);
            speak(
                t('assistant.intro',
                    'I am Mitra, your assistant for managing appointments. You can also say "Hey Mitra" any time to talk to me hands-free.'),
                { then: () => listenAgain(150) },
            );
            if (!ttsSupported || !speakReplies) listenAgain(150);
            return;   // the greeting hands the mic over when it finishes
        }
        try {
            // Let the browser clean the audio before it reaches us. A clinic
            // room has other voices, doors and a speakerphone; plain
            // `audio: true` hands all of it to Whisper, which then transcribes
            // the room instead of the doctor.
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
            });
            const rec = new MediaRecorder(stream, MediaRecorder.isTypeSupported('audio/webm') ? { mimeType: 'audio/webm' } : undefined);
            chunksRef.current = [];
            discardRef.current = false;

            rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
            rec.onstop = async () => {
                monitorRef.current?.stop?.();
                stream.getTracks().forEach((tr) => tr.stop());
                setRecording(false);
                recordingRef.current = false;

                const blob = new Blob(chunksRef.current, { type: rec.mimeType || 'audio/webm' });
                if (discardRef.current || blob.size < 1200) {
                    // Silence only. Do not bother the API, and end the loop so
                    // an empty room does not keep the mic cycling forever.
                    endHandsFree();
                    return;
                }
                setTranscribing(true);
                try {
                    // Explicit setting wins; on 'auto' use the language the
                    // assistant last replied in, which is the language this
                    // conversation is being held in.
                    const hint = speechLang !== 'auto'
                        ? speechLang
                        : (convLangRef.current || null);
                    const res = await dispatch(sendAssistantVoice({ blob, language: hint })).unwrap();
                    const text = (res?.text || '').trim();
                    if (!text) { endHandsFree(); return; }
                    if (STOP_PHRASE.test(text)) endHandsFree();
                    await send(text);
                } catch {
                    endHandsFree();
                    setMessages((m) => [...m, { from: 'bot', text: t('assistant.voice_error', 'Sorry — I could not understand the audio. Please try again.') }]);
                } finally {
                    setTranscribing(false);
                }
            };

            recorderRef.current = rec;
            rec.start();
            setRecording(true);
            recordingRef.current = true;

            monitorSilence(stream, (discard) => {
                discardRef.current = discard;
                try { rec.stop(); } catch { /* noop */ }
            });
        } catch {
            endHandsFree();
            setMessages((m) => [...m, { from: 'bot', text: t('assistant.mic_denied', 'Microphone access was denied — please allow it in the browser.') }]);
        }
    };
    micRef.current = toggleMic;

    return (
        <>
            {!open && (
                <button
                    type="button"
                    data-testid="assistant-fab"
                    onClick={() => setOpen(true)}
                    className="fixed bottom-5 right-5 z-[900] w-14 h-14 rounded-full shadow-xl flex items-center justify-center text-white bg-gradient-to-br from-[#4ABDBD] to-[#2D8A8A] hover:scale-105 transition-transform"
                    title={t('assistant.open', 'AI Assistant')}
                >
                    <Sparkles className="w-6 h-6" />
                    {wakeListening && (
                        <span className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-red-500 ring-2 ring-white animate-pulse" />
                    )}
                </button>
            )}

            {open && (
                <div
                    data-testid="assistant-drawer"
                    className="fixed bottom-5 right-5 z-[900] w-[370px] max-w-[92vw] h-[520px] max-h-[80vh] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden"
                >
                    {/* Header */}
                    <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-[#4ABDBD] to-[#2D8A8A] text-white">
                        <div className="flex items-center gap-2">
                            <Bot className="w-5 h-5" />
                            <span className="font-bold text-sm">{t('assistant.title', 'Mitra One Care AI Assistant')}</span>
                            {handsFree && (
                                <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-white/25" data-testid="assistant-handsfree">
                                    {t('assistant.handsfree', 'hands-free')}
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-1">
                            {(speaking || handsFree) && (
                                <button
                                    type="button"
                                    data-testid="assistant-stop-voice"
                                    onClick={() => { stopSpeaking(); endHandsFree(); if (recordingRef.current) { discardRef.current = true; try { recorderRef.current?.stop(); } catch { /* noop */ } } }}
                                    title={t('assistant.stop_voice', 'Stop voice')}
                                    className="p-1 rounded-lg hover:bg-white/15"
                                >
                                    <VolumeX className="w-5 h-5" />
                                </button>
                            )}
                            <button type="button" data-testid="assistant-close" onClick={closePanel} className="p-1 rounded-lg hover:bg-white/15">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                    </div>

                    {/* Messages */}
                    <div className="flex-1 overflow-y-auto custom-scrollbar p-3 space-y-2 bg-gray-50/60" data-testid="assistant-messages">
                        {messages.length === 0 && (
                            <div className="text-center text-gray-400 text-sm mt-10 px-4">
                                <Sparkles className="w-7 h-7 mx-auto mb-2 text-[#4ABDBD]/60" />
                                {t('assistant.hint', 'Hi! Ask me anything about your appointments.')}
                            </div>
                        )}
                        {messages.map((m, i) => {
                            // While this bubble is being spoken, show only the
                            // words said so far — the text and the voice arrive
                            // together instead of the text landing whole and
                            // then being read out.
                            const revealing = m.from === 'bot' && reveal && reveal.key === i;
                            const shownText = revealing
                                ? wordTokens(m.text).slice(0, reveal.shown).join('')
                                : m.text;
                            return (
                            <div key={i} className={`flex flex-col ${m.from === 'user' ? 'items-end' : 'items-start'}`}>
                                <div className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm whitespace-pre-line ${
                                    m.from === 'user'
                                        ? 'bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white rounded-br-sm'
                                        : 'bg-white border border-gray-200 text-gray-700 rounded-bl-sm'
                                }`}>
                                    {shownText}
                                    {revealing && <span className="inline-block w-1 h-3.5 ml-0.5 align-middle bg-[#4ABDBD] animate-pulse" />}
                                </div>
                                {/* Buttons appear once the sentence has finished — offering
                                    choices before they have been read is confusing. */}
                                {m.from === 'bot' && i === messages.length - 1 && !busy && !revealing && m.options?.length > 0 && (
                                    <div className="flex flex-wrap gap-1.5 mt-1.5 max-w-[92%]" data-testid="assistant-options">
                                        {m.options.map((opt) => (
                                            <button
                                                key={opt}
                                                type="button"
                                                onClick={() => { stopSpeaking(); send(opt); }}
                                                className="px-3 py-1.5 rounded-full text-xs font-medium border border-[#4ABDBD] text-[#2D8A8A] bg-white hover:bg-[#4ABDBD]/10 transition-colors"
                                            >
                                                {opt}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                            );
                        })}
                        {busy && (
                            <div className="flex justify-start">
                                <div className="px-3 py-2 rounded-2xl bg-white border border-gray-200">
                                    <Loader2 className="w-4 h-4 animate-spin text-[#3AA8A8]" />
                                </div>
                            </div>
                        )}
                        <div ref={bottomRef} />
                    </div>

                    {/* Input */}
                    <div className="relative p-3 border-t border-gray-100 bg-white flex items-center gap-1.5">
                        {settingsOpen && (
                            <>
                                <button
                                    type="button"
                                    aria-label={t('assistant.close_settings', 'Close settings')}
                                    className="fixed inset-0 z-[901] cursor-default"
                                    onClick={() => setSettingsOpen(false)}
                                />
                                <div
                                    data-testid="assistant-voice-settings"
                                    className="absolute bottom-14 left-3 z-[902] w-[248px] bg-white rounded-xl shadow-2xl border border-gray-200 p-3 space-y-3"
                                >
                                    <div className="text-xs font-bold text-gray-700">
                                        {t('assistant.voice_settings', 'Voice settings')}
                                    </div>

                                    <div>
                                        <div className="text-[11px] text-gray-500 mb-1">{t('assistant.reply_voice', 'Reply voice')}</div>
                                        <div className="flex rounded-lg border border-gray-200 overflow-hidden">
                                            {['female', 'male'].map((g) => (
                                                <button
                                                    key={g}
                                                    type="button"
                                                    data-testid={`assistant-voice-${g}`}
                                                    onClick={() => {
                                                        setVoiceGender(g);
                                                        persist('assistantVoiceGender', g);
                                                        stopSpeaking();
                                                        // Preview immediately — the available voices differ per
                                                        // device, so the only honest way to choose is to hear it.
                                                        const { voice: v, genderMatched } = pickVoice(voices, i18n.language, g);
                                                        if (ttsSupported) {
                                                            try {
                                                                const u = new SpeechSynthesisUtterance(t('assistant.voice_sample_hi', 'Hello, I am Mitra. How can I help you today?'));
                                                                if (v) { u.voice = v; u.lang = v.lang; }
                                                                u.rate = voiceRate;
                                                                u.pitch = genderMatched
                                                                    ? (g === 'male' ? 0.95 : 1.15)
                                                                    : (g === 'male' ? 0.75 : 1.4);
                                                                window.speechSynthesis.speak(u);
                                                            } catch { /* noop */ }
                                                        }
                                                    }}
                                                    className={`flex-1 py-1.5 text-xs font-semibold transition-colors ${
                                                        voiceGender === g ? 'bg-[#4ABDBD] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                                                    }`}
                                                >
                                                    {g === 'female' ? t('assistant.female', 'Female') : t('assistant.male', 'Male')}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Which language the USER speaks */}
                                    <div>
                                        <div className="text-[11px] text-gray-500 mb-1">
                                            {t('assistant.speech_language', 'I speak')}
                                        </div>
                                        <select
                                            data-testid="assistant-speech-lang"
                                            value={speechLang}
                                            onChange={(e) => { setSpeechLang(e.target.value); persist('assistantSpeechLang', e.target.value); }}
                                            className="tt-select w-full text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]/40"
                                        >
                                            <option value="auto">{t('assistant.lang_auto', 'Auto-detect')}</option>
                                            <option value="en">English</option>
                                            <option value="ta">தமிழ் — Tamil</option>
                                            <option value="hi">हिन्दी — Hindi</option>
                                            <option value="te">తెలుగు — Telugu</option>
                                            <option value="kn">ಕನ್ನಡ — Kannada</option>
                                            <option value="ml">മലയാളം — Malayalam</option>
                                            <option value="bn">বাংলা — Bengali</option>
                                            <option value="fr">Français — French</option>
                                        </select>
                                        <p className="text-[10px] leading-snug text-gray-400 mt-1">
                                            {t('assistant.speech_language_hint',
                                                'Pick your language if auto-detect writes your speech in the wrong one.')}
                                        </p>
                                    </div>

                                    {/* Speaking speed */}
                                    <div>
                                        <div className="flex items-center justify-between mb-1">
                                            <span className="text-[11px] text-gray-500">{t('assistant.voice_speed', 'Speaking speed')}</span>
                                            <span className="text-[10px] font-semibold text-[#2D8A8A]">{voiceRate.toFixed(2)}×</span>
                                        </div>
                                        <input
                                            type="range"
                                            data-testid="assistant-voice-rate"
                                            min={RATE_MIN}
                                            max={RATE_MAX}
                                            step="0.05"
                                            value={voiceRate}
                                            onChange={(e) => {
                                                const r = parseFloat(e.target.value);
                                                setVoiceRate(r);
                                                persist('assistantVoiceRate', r);
                                            }}
                                            // Speak a sample on release so the speed can be judged by ear
                                            // rather than by a number.
                                            onMouseUp={() => speak(t('assistant.voice_sample', 'This is how fast I will speak.'))}
                                            onTouchEnd={() => speak(t('assistant.voice_sample', 'This is how fast I will speak.'))}
                                            className="w-full accent-[#4ABDBD]"
                                        />
                                        <div className="flex justify-between text-[9px] text-gray-400 -mt-0.5">
                                            <span>{t('assistant.slower', 'slower')}</span>
                                            <span>{t('assistant.faster', 'faster')}</span>
                                        </div>
                                    </div>

                                    <label className="flex items-center justify-between gap-2 cursor-pointer">
                                        <span className="text-xs text-gray-700">{t('assistant.speak_replies', 'Speak replies aloud')}</span>
                                        <input
                                            type="checkbox"
                                            data-testid="assistant-speak-toggle"
                                            checked={speakReplies}
                                            onChange={(e) => { setSpeakReplies(e.target.checked); persist('assistantSpeak', e.target.checked); }}
                                            className="w-4 h-4 accent-[#4ABDBD]"
                                        />
                                    </label>

                                    <label className={`flex items-center justify-between gap-2 ${SpeechRecognitionCtor ? 'cursor-pointer' : 'opacity-50'}`}>
                                        <span className="text-xs text-gray-700">
                                            {t('assistant.wake_word', 'Listen for “Hey Mitra”')}
                                        </span>
                                        <input
                                            type="checkbox"
                                            data-testid="assistant-wake-toggle"
                                            disabled={!SpeechRecognitionCtor}
                                            checked={wakeWord}
                                            onChange={(e) => { setWakeWord(e.target.checked); persist('assistantWake', e.target.checked); }}
                                            className="w-4 h-4 accent-[#4ABDBD]"
                                        />
                                    </label>
                                    <p className="text-[10px] leading-snug text-gray-400">
                                        {SpeechRecognitionCtor
                                            ? t('assistant.wake_hint', 'Keeps the microphone on while this tab is open. Say “Hey Mitra”, then just talk — it stops when you stop.')
                                            : t('assistant.wake_unsupported', 'This browser does not support wake words. Chrome or Edge is required.')}
                                    </p>
                                    {wakeWord && SpeechRecognitionCtor && (
                                        <div className="rounded-lg bg-gray-50 border border-gray-100 px-2 py-1.5">
                                            <div className="flex items-center gap-1.5 text-[10px] font-semibold text-gray-500">
                                                <span className={`w-1.5 h-1.5 rounded-full ${wakeListening ? 'bg-red-500 animate-pulse' : 'bg-gray-300'}`} />
                                                {wakeListening
                                                    ? t('assistant.wake_on', 'Microphone on — heard:')
                                                    : t('assistant.wake_starting', 'Starting…')}
                                            </div>
                                            <div className="text-[10px] text-gray-400 italic truncate mt-0.5" data-testid="assistant-last-heard">
                                                {lastHeard || t('assistant.wake_nothing', 'nothing yet')}
                                            </div>
                                        </div>
                                    )}
                                    {!ttsSupported && (
                                        <p className="text-[10px] leading-snug text-amber-600">
                                            {t('assistant.tts_unsupported', 'This browser cannot speak replies.')}
                                        </p>
                                    )}
                                    {missingVoiceLang && (
                                        <p className="text-[10px] leading-snug text-amber-600" data-testid="assistant-missing-voice">
                                            {t('assistant.no_voice_for_lang',
                                                'No “{{lang}}” voice is installed on this device, so replies in that language may be silent. Add it in your system’s language / text-to-speech settings.',
                                                { lang: missingVoiceLang.toUpperCase() })}
                                        </p>
                                    )}
                                </div>
                            </>
                        )}

                        {/* Reply-language picker. Separate from the cog because it
                            changes what the assistant SAYS, not how it sounds. */}
                        {langOpen && (
                            <>
                                <button
                                    type="button"
                                    aria-label={t('assistant.close_languages', 'Close languages')}
                                    className="fixed inset-0 z-[901] cursor-default"
                                    onClick={() => setLangOpen(false)}
                                />
                                <div
                                    data-testid="assistant-language-menu"
                                    className="absolute bottom-14 left-3 z-[902] w-[228px] max-h-[260px] overflow-y-auto bg-white rounded-xl shadow-2xl border border-gray-200 p-2"
                                >
                                    <div className="px-2 pt-1 pb-2 text-xs font-bold text-gray-700">
                                        {t('assistant.reply_language', 'Assistant replies in')}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => { setReplyLang(''); persist('assistantReplyLang', ''); setLangOpen(false); }}
                                        className={`w-full text-left px-3 py-2 rounded-lg text-xs transition-colors ${
                                            !replyLang ? 'bg-[#4ABDBD] text-white font-semibold' : 'text-gray-700 hover:bg-gray-50'
                                        }`}
                                    >
                                        {t('assistant.lang_match', 'Match my message')}
                                    </button>
                                    {langs.map((l) => (
                                        <button
                                            key={l.code}
                                            type="button"
                                            data-testid={`assistant-lang-${l.code}`}
                                            onClick={() => { setReplyLang(l.name); persist('assistantReplyLang', l.name); setLangOpen(false); }}
                                            className={`w-full text-left px-3 py-2 rounded-lg text-xs transition-colors ${
                                                replyLang === l.name ? 'bg-[#4ABDBD] text-white font-semibold' : 'text-gray-700 hover:bg-gray-50'
                                            }`}
                                        >
                                            {l.name}
                                        </button>
                                    ))}
                                    {!langs.length && (
                                        <div className="px-3 py-2 text-[10px] text-gray-400">
                                            {t('assistant.lang_loading', 'Loading languages…')}
                                        </div>
                                    )}
                                    <p className="px-2 pt-2 text-[10px] leading-snug text-gray-400">
                                        {t('assistant.reply_language_hint', 'Applies to this assistant only.')}
                                    </p>
                                </div>
                            </>
                        )}

                        <button
                            type="button"
                            data-testid="assistant-language"
                            onClick={() => { setLangOpen((s) => !s); setSettingsOpen(false); }}
                            title={t('assistant.reply_language', 'Assistant replies in')}
                            className={`relative p-2 rounded-full transition-colors ${
                                langOpen ? 'bg-[#4ABDBD]/15 text-[#2D8A8A]' : 'text-gray-400 hover:text-[#3AA8A8] hover:bg-[#4ABDBD]/10'
                            }`}
                        >
                            <Languages className="w-5 h-5" />
                            {replyLang && (
                                <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-[#4ABDBD] ring-2 ring-white" />
                            )}
                        </button>

                        <button
                            type="button"
                            data-testid="assistant-settings"
                            onClick={() => { setSettingsOpen((s) => !s); setLangOpen(false); }}
                            title={t('assistant.voice_settings', 'Voice settings')}
                            className={`p-2 rounded-full transition-colors ${
                                settingsOpen ? 'bg-[#4ABDBD]/15 text-[#2D8A8A]' : 'text-gray-400 hover:text-[#3AA8A8] hover:bg-[#4ABDBD]/10'
                            }`}
                        >
                            <Settings className="w-5 h-5" />
                        </button>

                        <button
                            type="button"
                            data-testid="assistant-mic"
                            onClick={toggleMic}
                            disabled={busy || transcribing}
                            title={recording ? t('assistant.stop_recording', 'Stop recording') : t('assistant.speak', 'Speak your message')}
                            className={`p-2 rounded-full transition-colors ${
                                recording
                                    ? 'bg-red-500 text-white animate-pulse'
                                    : transcribing
                                        ? 'text-gray-300 cursor-wait'
                                        : 'text-[#3AA8A8] hover:bg-[#4ABDBD]/10'
                            }`}
                        >
                            {recording ? <Square className="w-5 h-5" /> : transcribing ? <Loader2 className="w-5 h-5 animate-spin" /> : <Mic className="w-5 h-5" />}
                        </button>
                        <input
                            data-testid="assistant-input"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                            placeholder={recording ? t('assistant.listening_now', 'Listening… just stop talking') : t('assistant.placeholder', 'Type a message…')}
                            className="flex-1 min-w-0 px-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]/40"
                        />
                        <button
                            type="button"
                            data-testid="assistant-send"
                            onClick={() => send()}
                            disabled={busy || !input.trim()}
                            className="p-2 rounded-full text-white bg-gradient-to-r from-[#4ABDBD] to-[#2D8A8A] disabled:opacity-40"
                        >
                            <Send className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            )}
        </>
    );
}
